import React, { useEffect, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { BluCloudThemeProvider } from '@bludata/components';
import { errorDialogDefaultFunction } from '@bludata/utility-functions';
import CloseIcon from '@mui/icons-material/Close';
import getDecodedJWTToken from '../login/msal/getDecodedJWTToken';
import { getIsImpersonation } from '../login/msal/MsalInstance';

interface BludataPasswordRequiredProps {
  show: boolean;
  handleClose: () => void;
  handleConfirm: (token: string) => void;
}

const BludataPasswordRequired: React.FC<BludataPasswordRequiredProps> = ({
  show,
  handleClose,
  handleConfirm,
}) => {
  const themeMode =
    window.document.querySelector('html')?.getAttribute('dark-mode') === 'true'
      ? 'dark'
      : 'light';
  const decodedJWT = getDecodedJWTToken();

  const isDebug = import.meta.env.DEV;
  const isImpersonation = getIsImpersonation();
  let loginUrl = '';
  let redirectUriLogin = '';

  if (!isImpersonation && !isDebug) {
    redirectUriLogin = `${
      import.meta.env.VITE_NX_PUBLIC_API_URL
    }b2ctemplates/shared/changePasswordRedirect.html`;

    const loginUrlObject = new URL(
      `https://${import.meta.env.VITE_NX_PUBLIC_YOUR_DOMAIN_NAME}/${
        import.meta.env.VITE_NX_PUBLIC_YOUR_TENANT_NAME
      }.onmicrosoft.com/oauth2/v2.0/authorize`,
    );
    loginUrlObject.searchParams.append(
      'p',
      import.meta.env.VITE_NX_PUBLIC_CHECK_PSW || '',
    );
    loginUrlObject.searchParams.append(
      'client_id',
      import.meta.env.VITE_NX_PUBLIC_YOUR_CLIENT_ID || '',
    );
    loginUrlObject.searchParams.append('nonce', 'defaultNonce');
    loginUrlObject.searchParams.append('redirect_uri', redirectUriLogin);
    loginUrlObject.searchParams.append('scope', 'openid');
    loginUrlObject.searchParams.append('response_type', 'id_token');
    loginUrlObject.searchParams.append('prompt', 'login');
    loginUrlObject.searchParams.append('consumerRef', decodedJWT.consumerRef);
    loginUrlObject.searchParams.append('userRef', decodedJWT.userRef);
    loginUrlObject.searchParams.append('onlyPsw', 'true');

    loginUrl = loginUrlObject.toString();
  }

  useEffect(() => {
    if (!show || isImpersonation) {
      return;
    }
    if (isDebug) {
      // In debug mode, la richiesta di password non è necessaria
      handleConfirm('token');
      return;
    }
    const handleIframeMessage = (event: MessageEvent) => {
      const redirectOrigin = new URL(redirectUriLogin).origin;

      if (event.origin !== redirectOrigin) {
        return;
      }

      if (typeof event.data !== 'object' || event.data === null) {
        return;
      }

      if (!event.data.token && !event.data.error) {
        console.warn('Messaggio ricevuto non riconosciuto:', event.data);
      }

      if (event.data && event.data.token) {
        // Token ricevuto con successo
        handleConfirm(event.data.token);
      } else if (event.data && event.data.error) {
        errorDialogDefaultFunction('Errore durante l’autenticazione.');
        handleClose();
      }
    };

    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [handleClose, handleConfirm, redirectUriLogin, isDebug, show]);

  return (
    <BluCloudThemeProvider themeMode={themeMode}>
      <Dialog
        open={show}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            width: '480px',
            height: '320px',
          },
          '& .MuiDialogContent-root': {
            overflow: 'hidden',
            display: 'flex',
          },
        }}
      >
        <DialogTitle>
          <Stack flexDirection="row" gap={1} alignItems="center">
            <Typography
              color="inherit"
              fontWeight="500"
              fontSize="1.25rem"
              letterSpacing="0.0075em"
              lineHeight="1.3em"
              sx={{ flex: 1 }}
            >
              Richiesta credenziali
            </Typography>
            {!isImpersonation && (
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon
                  sx={{
                    color: 'var(--reverse-color)',
                  }}
                  fontSize="small"
                />
              </IconButton>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 1 }}>
          {!isImpersonation && (
            <Stack
              flex={1}
              sx={{
                '& iframe': {
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px',
                },
              }}
            >
              <iframe
                src={loginUrl}
                title="Login"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </Stack>
          )}
          {isImpersonation && (
            <Stack
              flex={1}
              justifyContent="center"
              alignItems="center"
              sx={{ textAlign: 'center' }}
            >
              <Typography variant="body1">
                <b>ATTENZIONE</b>
                <br />
                Funzione solitamente soggetta a richiesta di autenticazione.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        {isImpersonation && (
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                handleClose();
              }}
            >
              Annulla
            </Button>
            <Button
              onClick={() => {
                handleConfirm('token'); // Simula il token per l'impersonazione
              }}
            >
              Continua
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </BluCloudThemeProvider>
  );
};

export default BludataPasswordRequired;
